import { z } from "zod";

// ── Dashboard Stats ──

export const DashboardStatsSchema = z.object({
    total_lists: z.number().int(),
    total_companies: z.number().int(),
    total_profiles: z.number().int(),
    tracked_profiles: z.number().int(),
    total_users: z.number().int(),
    profiles_with_email: z.number().int(),
    lists_by_status: z.record(z.string(), z.number().int()),
});
export type DashboardStats = z.infer<typeof DashboardStatsSchema>;

export const DashboardStatsResponseSchema = z.object({
    success: z.boolean(),
    data: DashboardStatsSchema,
});
export type DashboardStatsResponse = z.infer<typeof DashboardStatsResponseSchema>;

// ── Country Distribution ──

export const CountryDistributionItemSchema = z.object({
    country: z.string(),
    count: z.number().int(),
});
export type CountryDistributionItem = z.infer<typeof CountryDistributionItemSchema>;

export const CountryDistributionResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(CountryDistributionItemSchema),
});
export type CountryDistributionResponse = z.infer<typeof CountryDistributionResponseSchema>;

// ── Top Compatible LPs (sample) ──

export const TopCompatibleLPSchema = z.object({
    id: z.string(),
    full_name: z.string(),
    profile_image_url: z.string().nullable(),
    company: z.string().nullable(),
});
export type TopCompatibleLP = z.infer<typeof TopCompatibleLPSchema>;

export const TopCompatibleLPsResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(TopCompatibleLPSchema),
});
export type TopCompatibleLPsResponse = z.infer<typeof TopCompatibleLPsResponseSchema>;

// ── Source Split ──

/** `invyo`: in at least one Invyo list of the licence; `external`: only in the client's imports (#633). */
export const SourceSplitItemSchema = z.object({
    source: z.enum(["invyo", "external"]),
    count: z.number().int(),
});
export type SourceSplitItem = z.infer<typeof SourceSplitItemSchema>;

export const SourceSplitResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(SourceSplitItemSchema),
});
export type SourceSplitResponse = z.infer<typeof SourceSplitResponseSchema>;

// ── Monthly LPs Added (#634) ──

/** `month` is `YYYY-MM` (UTC); months with no addition are absent. */
export const MonthlyAddedItemSchema = z.object({
    month: z.string(),
    count: z.number().int(),
});
export type MonthlyAddedItem = z.infer<typeof MonthlyAddedItemSchema>;

export const MonthlyAddedResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(MonthlyAddedItemSchema),
});
export type MonthlyAddedResponse = z.infer<typeof MonthlyAddedResponseSchema>;

// ── Latest delivery ──

/** The last list Invyo delivered to the licence (null before the first delivery). */
export const LatestDeliverySchema = z.object({
    id: z.string(),
    name: z.string(),
    sent_at: z.string(),
    profile_count: z.number().int(),
});
export type LatestDelivery = z.infer<typeof LatestDeliverySchema>;

/**
 * WRAPPED in `{ delivery }`, never `data: null`: the front's api client unwraps
 * with `body.data ?? body`, so a null `data` would reach the caller as the whole
 * envelope: a truthy object read as a delivery (see `PendingEmailChangeResponseSchema`).
 */
export const LatestDeliveryResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        delivery: LatestDeliverySchema.nullable(),
    }),
});
export type LatestDeliveryResponse = z.infer<typeof LatestDeliveryResponseSchema>;
